import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Target, Compass, TrendingUp, Eye } from 'lucide-react';

const MissionSection = () => {
  const { language, t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const pillars = [
    { 
      icon: Compass, 
      text: language === 'fr' ? 'Clarifier leur positionnement' : 'Clarify their positioning' 
    },
    { 
      icon: Target, 
      text: language === 'fr' ? 'Construire une identité forte' : 'Build a strong identity' 
    },
    { 
      icon: TrendingUp, 
      text: language === 'fr' ? 'Développer une stratégie cohérente' : 'Develop a coherent strategy' 
    },
    { 
      icon: Eye, 
      text: language === 'fr' ? 'Amplifier leur visibilité' : 'Amplify their visibility' 
    },
  ];

  return (
    <section
      id="mission"
      ref={ref}
      className="py-24 md:py-32 bg-black text-white"
      data-testid="mission-section"
    >
      <div className="container-custom">
        <div className="max-w-4xl mx-auto text-center">
          {/* Section Header */}
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            className="inline-block text-xs font-semibold tracking-[0.3em] uppercase text-neutral-400 mb-4"
          >
            {language === 'fr' ? 'Notre raison d\'être' : 'Our purpose'}
          </motion.span>
          
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
            className="heading-xl text-4xl md:text-5xl lg:text-6xl mb-8 text-white"
            data-testid="mission-title"
          >
            {t('mission.title')}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="text-xl md:text-2xl text-neutral-300 leading-relaxed mb-12"
          >
            {language === 'fr' 
              ? 'Structurer l\'image des marques ambitieuses et transformer leur présence digitale en levier de croissance.'
              : 'Structure the image of ambitious brands and transform their digital presence into a growth lever.'}
          </motion.p>

          {/* Pillars Grid */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8"
          >
            {pillars.map((pillar, index) => {
              const IconComponent = pillar.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="flex flex-col items-center text-center"
                  data-testid={`mission-pillar-${index}`}
                >
                  <div className="w-14 h-14 border border-neutral-700 rounded-xl flex items-center justify-center mb-4 group-hover:border-white transition-colors">
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-sm md:text-base text-neutral-300">
                    {pillar.text}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default MissionSection;
